import { useState } from 'react';
import { Modal, Button, Input, Checkbox } from '@/components/ui';
import { useSettingsStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';
import type { Category, ItemType, CreateCategoryInput, UpdateCategoryInput } from '@perfin/shared';
import { CATEGORY_COLORS } from '@perfin/shared';

interface CategoryFormProps {
  category: Category | null;
  onClose: () => void;
}

const ITEM_TYPES: { value: ItemType; label: string }[] = [
  { value: 'task', label: 'Tasks' },
  { value: 'transaction', label: 'Transactions' },
  { value: 'note', label: 'Notes' },
  { value: 'keyDate', label: 'Key Dates' },
];

export function CategoryForm({ category, onClose }: CategoryFormProps) {
  const { addCategory, editCategory } = useSettingsStore();
  const toast = useToast();

  const [name, setName] = useState(category?.name || '');
  const [color, setColor] = useState(category?.color || 'gray');
  const [itemTypes, setItemTypes] = useState<ItemType[]>(
    category?.itemTypes || ['task', 'transaction', 'note', 'keyDate']
  );
  const [saving, setSaving] = useState(false);

  const isEditing = !!category;

  const toggleItemType = (type: ItemType) => {
    setItemTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Category name is required');
      return;
    }

    if (itemTypes.length === 0) {
      toast.error('Select at least one item type');
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        const input: UpdateCategoryInput = { name: name.trim(), color, itemTypes };
        await editCategory(category.id, input);
        toast.success('Category updated');
      } else {
        const input: CreateCategoryInput = { name: name.trim(), color, itemTypes };
        await addCategory(input);
        toast.success('Category created');
      }
      onClose();
    } catch {
      toast.error(isEditing ? 'Failed to update category' : 'Failed to create category');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEditing ? 'Edit Category' : 'Add Category'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Category Name */}
        <Input
          label="Category Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Home Improvement"
          autoFocus
        />

        {/* Color Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Color
          </label>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setColor(c.value)}
                className={`w-8 h-8 rounded-full ${c.bg} border-2 transition-all ${
                  color === c.value
                    ? 'border-gray-800 scale-110'
                    : 'border-transparent hover:scale-105'
                }`}
                title={c.label}
              />
            ))}
          </div>
        </div>

        {/* Item Types */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Applicable Item Types
          </label>
          <div className="space-y-2">
            {ITEM_TYPES.map((type) => (
              <Checkbox
                key={type.value}
                label={type.label}
                checked={itemTypes.includes(type.value)}
                onChange={() => toggleItemType(type.value)}
              />
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Select which types of items can use this category
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving}>
            {isEditing ? 'Save Changes' : 'Add Category'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
